import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertTriangle, Camera, Car, Check, CheckCircle, Eye, FileText, Gauge,
  Loader2, Pencil, Shield, X, Image, RefreshCw, MapPin, Calendar, Scale,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { TabLoader } from './TabLoader';
import { toast } from 'sonner';
import { inspectionsApi } from '@/services/api';
import { Section, EditableField } from './helpers';

const VehicleTab = ({
  editData, updateField, canEdit, inspection, liveProgressData,
  onInspectionUpdated, kmExtractionResult,
  extractingKM, extractKMFromDashboardPhoto, editingKmReading,
  setEditingKmReading, manualKmValue, setManualKmValue, loadingQAImage,
}) => {
  // Derive the effective KMS value: editData > inspection > AI extraction
  const effectiveKms = editData.kms_driven || inspection?.kms_driven || inspection?.ai_extracted_kms || kmExtractionResult?.km_reading || 0;
  // Local state for image/photo dialogs
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showDashboardPhotoModal, setShowDashboardPhotoModal] = useState(false);
  const [loadingDashboardPhoto, setLoadingDashboardPhoto] = useState(false);
  const [dashboardPhotoData, setDashboardPhotoData] = useState(null);
  const [showQAImageModal, setShowQAImageModal] = useState(false);
  const [qaImageData, setQaImageData] = useState(null);
  const [qaImageTitle, setQaImageTitle] = useState('');
  
  // Challan state
  const [challanData, setChallanData] = useState(null);
  const [fetchingChallans, setFetchingChallans] = useState(false);

  // RC data is cached permanently in db.vehicles, so the reg/insurance fields
  // can be months old. This is the ONLY place a rep can knowingly pay for a
  // fresh provider call.
  const [refreshingRc, setRefreshingRc] = useState(false);

  const refreshRcData = useCallback(async () => {
    if (!inspection?.id) return;
    setRefreshingRc(true);
    try {
      const { data } = await inspectionsApi.fetchVaahanData(inspection.id, { forceRefresh: true });
      if (data?.success) {
        toast.success('RC data refreshed from the RTO provider');
        if (onInspectionUpdated && data.inspection) onInspectionUpdated(data.inspection);
      } else {
        toast.error(data?.error || 'Could not refresh RC data');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not refresh RC data');
    } finally {
      setRefreshingRc(false);
    }
  }, [inspection?.id, onInspectionUpdated]);  
  // Initialize challan data from inspection
  useEffect(() => {
    if (inspection?.challan_data) {
      setChallanData(inspection.challan_data);
    }
  }, [inspection?.challan_data]);
  
  // Fetch challans from Surepass API
  const handleFetchChallans = async () => {
    if (!inspection?.id) return;
    setFetchingChallans(true);
    try {
      const response = await inspectionsApi.fetchChallans(inspection.id);
      if (response.data?.success) {
        setChallanData(response.data.data);
        toast.success(`Found ${response.data.data.total_challans} challan(s)`);
      } else {
        toast.error(response.data?.error || 'Failed to fetch challans');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch challan data');
    } finally {
      setFetchingChallans(false);
    }
  };
  // Get dashboard odometer photo URL from inspection data.
  // Prefer the inspection's OWN answer (correct question ids) so the modal and
  // the KM-extraction always use this car's photo — never a stale liveProgress
  // photo from a previously-opened inspection.
  const getDashboardOdometerPhoto = useCallback(() => {
    const dashQids = [
      'f93f7cea-9664-4e67-8f42-075e4d990a5e',
      'd72d0a59-f621-4afa-8f2e-191f2efe31ef',
      '3ca03df7-458b-4da4-b0d9-f938614c4a12',
    ];
    const answers = inspection?.inspection_answers || {};
    for (const qid of dashQids) {
      const a = answers[qid];
      if (a) {
        const url = typeof a === 'string' ? a : a?.answer;
        if (url) return url;
      }
    }
    if (inspection?.ai_extracted_kms_image_url) return inspection.ai_extracted_kms_image_url;
    const categories = liveProgressData?.categories || [];
    for (const cat of categories) {
      const questions = cat.questions || [];
      const questionsArr = Array.isArray(questions) ? questions : Object.values(questions);
      for (const q of questionsArr) {
        const qtext = (q.question_text || '').toLowerCase();
        if ((qtext.includes('dashboard') && qtext.includes('odometer')) || qtext === 'dashboard odometer photo') {
          const answer = q.answer;
          if (answer && typeof answer === 'string' && (answer.startsWith('data:image') || answer.includes('http') || answer.includes('firebase') || answer.includes('media_ref:'))) {
            return answer;
          }
        }
      }
    }
    return null;
  }, [inspection, liveProgressData]);

  // Load dashboard photo when modal opens
  useEffect(() => {
    const loadDashboardPhoto = async () => {
      if (!showDashboardPhotoModal) return;
      const imageUrl = getDashboardOdometerPhoto();
      if (!imageUrl) { setDashboardPhotoData(null); return; }
      if (imageUrl.startsWith('media_ref:')) {
        setLoadingDashboardPhoto(true);
        try {
          const mediaRefId = imageUrl.replace('media_ref:', '');
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inspection-media/${mediaRefId}`);
          if (response.data?.data) setDashboardPhotoData(response.data.data);
        } catch (error) { console.error('Failed to load dashboard photo:', error); }
        finally { setLoadingDashboardPhoto(false); }
      } else { setDashboardPhotoData(imageUrl); }
    };
    loadDashboardPhoto();
  }, [showDashboardPhotoModal, getDashboardOdometerPhoto]);

  return (
    <>
            <TabsContent value="vehicle" className="space-y-3 mt-0">
              {!inspection?.id ? <TabLoader message="Loading vehicle details..." /> : (<>
              {/* TWO-COLUMN LAYOUT */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* LEFT: Vehicle & Technical Details + KM Reading + RTO inside */}
                <Section title="Vehicle & Technical Details" icon={Car} defaultOpen={true}>
                  {/* KM Reading - Blue accent strip inside section */}
                  <div className="rounded-lg p-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 mb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                          <Gauge className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="text-[10px] text-blue-600 font-medium uppercase tracking-wide">Odometer</p>
                          {extractingKM ? (
                            <div className="flex items-center gap-1.5 text-blue-600">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              <span className="text-xs">Extracting...</span>
                            </div>
                          ) : editingKmReading ? (
                            <div className="flex items-center gap-1.5">
                              <Input type="number" value={manualKmValue} onChange={(e) => setManualKmValue(e.target.value)} className="w-24 h-7 text-sm font-bold" placeholder="KM" data-testid="km-manual-input" />
                              <span className="text-blue-600 text-xs">km</span>
                              <Button size="sm" className="h-7 w-7 p-0 bg-green-600 hover:bg-green-700" data-testid="km-save-btn" onClick={async () => {
                                if (!manualKmValue) return;
                                try {
                                  const response = await inspectionsApi.updateKmReading(inspection.id, { km_reading: parseInt(manualKmValue), source: 'manual_override', previous_value: inspection?.ai_extracted_kms || inspection?.kms_driven, reason: 'Manual correction by admin' });
                                  if (response.data) { toast.success(`KM updated to ${parseInt(manualKmValue).toLocaleString()} km`); setEditingKmReading(false); updateField('kms_driven', parseInt(manualKmValue)); if (onInspectionUpdated) onInspectionUpdated(response.data); }
                                } catch (error) { toast.error('Failed to update KM reading'); }
                              }}><Check className="h-3 w-3" /></Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingKmReading(false); setManualKmValue(''); }}><X className="h-3 w-3" /></Button>
                            </div>
                          ) : (effectiveKms > 0) ? (
                            <div className="flex items-center gap-1.5">
                              <span className="text-xl font-bold text-blue-900">{effectiveKms.toLocaleString()}</span>
                              <span className="text-blue-600 text-xs">km</span>
                              {inspection?.ai_extracted_kms_confidence && (
                                <Badge variant="outline" className="bg-white text-blue-700 border-blue-300 text-[10px] h-4">{Math.round(inspection.ai_extracted_kms_confidence * 100)}%</Badge>
                              )}
                              {(inspection?.ai_extracted_kms_needs_review || kmExtractionResult?.needs_review) && (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-[10px] h-4 animate-pulse">Needs Review</Badge>
                              )}
                            </div>
                          ) : kmExtractionResult?.success === false || inspection?.ai_extracted_kms_error ? (
                            <div className="text-amber-700 text-xs">
                              <span>{inspection?.ai_extracted_kms_error || kmExtractionResult?.error || 'AI extraction failed'}</span>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">Not extracted yet</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {!editingKmReading && canEdit && (
                          <Button size="sm" variant="ghost" className="text-[10px] text-gray-500 hover:text-blue-600 h-7 px-1.5" data-testid="km-edit-btn" onClick={() => { setManualKmValue(effectiveKms || ''); setEditingKmReading(true); }}>
                            <Pencil className="h-3 w-3 mr-0.5" />{effectiveKms ? 'Edit' : 'Enter KM'}
                          </Button>
                        )}
                        {getDashboardOdometerPhoto() && (
                          <Button size="sm" variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-100 h-7 px-2 text-[10px]" onClick={() => setShowDashboardPhotoModal(true)}>
                            <Eye className="h-3 w-3 mr-0.5" />View Photo
                          </Button>
                        )}
                        {!extractingKM && !inspection?.ai_extracted_kms && (
                          <Button size="sm" variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-100 h-7 px-2 text-[10px]" onClick={() => {
                            // Always resolve from THIS inspection's own photo (own answer first).
                            // The backend also re-validates ownership, so a stale/foreign URL is rejected.
                            extractKMFromDashboardPhoto(getDashboardOdometerPhoto() || null);
                          }} disabled={extractingKM}>
                            <Camera className="h-3 w-3 mr-0.5" />Extract KM
                          </Button>
                        )}
                      </div>
                    </div>
                    {inspection?.kms_driven && inspection?.ai_extracted_kms && inspection.kms_driven !== inspection.ai_extracted_kms && (
                      <div className="flex items-center gap-2 mt-1.5 px-2 py-1 bg-amber-50 rounded border border-amber-200 text-[10px]">
                        <span className="text-amber-800">Current: <strong>{inspection.kms_driven.toLocaleString()} km</strong></span>
                        {canEdit && (
                          <Button size="sm" variant="ghost" className="h-4 px-1 text-blue-600 hover:bg-blue-100 text-[10px]" onClick={async () => {
                            try {
                              const response = await inspectionsApi.updateKmReading(inspection.id, { km_reading: inspection.ai_extracted_kms, source: 'reset_to_ai', previous_value: inspection.kms_driven, reason: 'Reset to AI extracted value' });
                              if (response.data) { toast.success('Reset to AI value'); if (onInspectionUpdated) onInspectionUpdated(response.data); }
                            } catch (error) { toast.error('Failed to reset'); }
                          }}>Reset to AI</Button>
                        )}
                      </div>
                    )}
                    {/* Validation flags warning */}
                    {(inspection?.ai_extracted_kms_needs_review || kmExtractionResult?.needs_review) && (
                      <div className="flex items-start gap-2 mt-1.5 px-2 py-1.5 bg-amber-50 rounded border border-amber-300 text-[10px]">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="text-amber-800 font-semibold">AI reading flagged for review:</span>
                          <ul className="text-amber-700 mt-0.5 space-y-0.5">
                            {(inspection?.ai_extracted_kms_validation_flags || kmExtractionResult?.validation_flags || []).map((flag, i) => (
                              <li key={i}>- {flag}</li>
                            ))}
                          </ul>
                          {(inspection?.ai_extracted_kms_all_readings || kmExtractionResult?.all_readings_seen || []).length > 1 && (
                            <p className="text-gray-600 mt-0.5">All readings seen: {(inspection?.ai_extracted_kms_all_readings || kmExtractionResult?.all_readings_seen).join(', ')}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {inspection?.vaahan_data && (
                    <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg" data-testid="rto-verified-banner">
                      <Shield className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="text-xs font-semibold text-green-800">RTO Verified</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        inspection.vaahan_data.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>{inspection.vaahan_data.status || 'ACTIVE'}</span>
                      {inspection?.vaahan_fetched_at && (
                        <span className="text-[10px] text-green-700/80" data-testid="rc-data-as-of">
                          RC data as of {new Date(inspection.vaahan_fetched_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 ml-auto text-[10px] border-green-300 text-green-800 hover:bg-green-100"
                        disabled={refreshingRc}
                        onClick={refreshRcData}
                        data-testid="refresh-rc-data-btn"
                        title="Re-fetch from the RTO provider. Costs a paid API call — the cached copy is free."
                      >
                        {refreshingRc
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <RefreshCw className="h-3 w-3" />}
                        <span className="ml-1">{refreshingRc ? 'Refreshing' : 'Refresh RC'}</span>
                      </Button>
                    </div>
                  )}
                  
                  {/* Vehicle fields grid */}
                  <div className="grid grid-cols-2 xl:grid-cols-3 gap-2">
                    <EditableField label="Reg. No." value={editData.car_number || inspection?.car_number} onChange={(val) => updateField('car_number', val)} disabled={!canEdit} />
                    <EditableField label="Engine No." value={editData.engine_number || inspection?.engine_number || inspection?.engine_no || inspection?.vaahan_data?.engine_number} onChange={(val) => updateField('engine_number', val)} disabled={!canEdit} />
                    <EditableField label="Chassis No." value={editData.chassis_number || inspection?.chassis_number || inspection?.chassis_no || inspection?.vaahan_data?.chassis_number} onChange={(val) => updateField('chassis_number', val)} disabled={!canEdit} />
                    <EditableField label="Make" value={editData.vehicle_make} onChange={(val) => updateField('vehicle_make', val)} disabled={!canEdit} />
                    <EditableField label="Model" value={editData.vehicle_model} onChange={(val) => updateField('vehicle_model', val)} disabled={!canEdit} />
                    <EditableField label="Year" value={editData.vehicle_year} onChange={(val) => updateField('vehicle_year', val)} type="number" disabled={!canEdit} />
                    <EditableField label="Colour" value={editData.vehicle_colour} onChange={(val) => updateField('vehicle_colour', val)} disabled={!canEdit} />
                    <EditableField label="Fuel Type" value={editData.fuel_type} onChange={(val) => updateField('fuel_type', val)} type="select" options={['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid', 'LPG']} disabled={!canEdit} />
                    <EditableField label="Transmission" value={editData.transmission} onChange={(val) => updateField('transmission', val)} type="select" options={['Manual', 'Automatic']} disabled={!canEdit} />
                    <EditableField label="Body Type" value={editData.body_type || inspection?.vaahan_data?.body_type} onChange={(val) => updateField('body_type', val)} disabled={!canEdit} />
                    <EditableField label="Vehicle Class" value={editData.vehicle_class || inspection?.vaahan_data?.vehicle_class} onChange={(val) => updateField('vehicle_class', val)} disabled={!canEdit} />
                    <EditableField label="Engine CC" value={editData.engine_cc || inspection?.vaahan_data?.cubic_capacity} onChange={(val) => updateField('engine_cc', val)} type="number" disabled={!canEdit} />
                    <EditableField label="Cylinders" value={editData.cylinders || inspection?.vaahan_data?.cylinders} onChange={(val) => updateField('cylinders', val)} type="number" disabled={!canEdit} />
                    <EditableField label="Seating" value={editData.seating_capacity || inspection?.vaahan_data?.seating_capacity} onChange={(val) => updateField('seating_capacity', val)} type="number" disabled={!canEdit} />
                    <EditableField label="Emission Norms" value={editData.emission_norms || inspection?.vaahan_data?.emission_norms} onChange={(val) => updateField('emission_norms', val)} disabled={!canEdit} />
                    <EditableField label="Gross Wt (kg)" value={editData.gross_weight || inspection?.vaahan_data?.gross_weight} onChange={(val) => updateField('gross_weight', val)} type="number" disabled={!canEdit} />
                    <EditableField label="Unladen Wt (kg)" value={editData.unladen_weight || inspection?.vaahan_data?.unladen_weight} onChange={(val) => updateField('unladen_weight', val)} type="number" disabled={!canEdit} />
                    <EditableField label="Wheelbase (mm)" value={editData.wheelbase || inspection?.vaahan_data?.wheelbase} onChange={(val) => updateField('wheelbase', val)} type="number" disabled={!canEdit} />
                  </div>
                </Section>

                {/* RIGHT: Registration + Compliance (Merged) */}
                <div className="space-y-3">
                  <Section title="Registration & Ownership" icon={FileText} defaultOpen={true}>
                    <div className="grid grid-cols-2 xl:grid-cols-3 gap-2">
                      <EditableField label="Mfg. Date" value={editData.manufacturing_date || inspection?.vaahan_data?.manufacturing_date} onChange={(val) => updateField('manufacturing_date', val)} disabled={!canEdit} />
                      <EditableField label="Reg. Date" value={editData.registration_date || inspection?.vaahan_data?.registration_date} onChange={(val) => updateField('registration_date', val)} disabled={!canEdit} />
                      <EditableField label="RC Expiry" value={editData.rc_expiry_date || inspection?.vaahan_data?.rc_expiry_date} onChange={(val) => updateField('rc_expiry_date', val)} disabled={!canEdit} />
                      <EditableField label="RTO Authority" value={editData.registration_authority || inspection?.vaahan_data?.registration_authority} onChange={(val) => updateField('registration_authority', val)} disabled={!canEdit} />
                      <EditableField label="Owner Count" value={editData.owners || inspection?.vaahan_data?.owner_count} onChange={(val) => updateField('owners', val)} type="number" disabled={!canEdit} />
                      <EditableField label="Current Owner" value={editData.owner_name || inspection?.vaahan_data?.owner_name} onChange={(val) => updateField('owner_name', val)} disabled={!canEdit} />
                    </div>
                  </Section>

                  <Section title="Insurance, RTO & PUCC" icon={Shield} defaultOpen={true}>
                    <div className="grid grid-cols-2 xl:grid-cols-3 gap-2">
                      <EditableField label="Insurance Status" value={editData.insurance_status} onChange={(val) => updateField('insurance_status', val)} type="select" options={['Active', 'Expired', 'Unknown']} disabled={!canEdit} />
                      <EditableField label="Insurer" value={editData.insurer_name || inspection?.vaahan_data?.insurance_company} onChange={(val) => updateField('insurer_name', val)} disabled={!canEdit} />
                      <EditableField label="Policy No." value={editData.policy_number || inspection?.vaahan_data?.insurance_policy_number} onChange={(val) => updateField('policy_number', val)} disabled={!canEdit} />
                      <EditableField label="Ins. Valid Upto" value={editData.insurance_expiry || inspection?.vaahan_data?.insurance_valid_upto} onChange={(val) => updateField('insurance_expiry', val)} disabled={!canEdit} />
                      <EditableField label="Policy Type" value={editData.policy_type} onChange={(val) => updateField('policy_type', val)} type="select" options={['Comprehensive', 'Third Party', 'Zero Dep']} disabled={!canEdit} />
                      <EditableField label="IDV Value" value={editData.idv_value} onChange={(val) => updateField('idv_value', val)} type="number" disabled={!canEdit} />
                      <EditableField label="RTO Verification" value={editData.rto_verification_status} onChange={(val) => updateField('rto_verification_status', val)} type="select" options={['VERIFIED', 'PENDING', 'FAILED', 'NOT_AVAILABLE']} disabled={!canEdit} />
                      <EditableField label="Financed" value={editData.is_financed ?? inspection?.vaahan_data?.financed} onChange={(val) => updateField('is_financed', val)} type="switch" disabled={!canEdit} />
                      <EditableField label="Financier" value={editData.hypothecation || inspection?.vaahan_data?.financer} onChange={(val) => updateField('hypothecation', val)} placeholder="Bank/Financier" disabled={!canEdit} />
                      <EditableField label="Blacklisted" value={editData.blacklist_status ?? inspection?.vaahan_data?.blacklist_status} onChange={(val) => updateField('blacklist_status', val)} type="switch" disabled={!canEdit} />
                      <EditableField label="Tax Valid Upto" value={editData.tax_valid_upto || inspection?.vaahan_data?.tax_valid_upto} onChange={(val) => updateField('tax_valid_upto', val)} disabled={!canEdit} />
                      <EditableField label="Fitness Upto" value={editData.fitness_upto || inspection?.vaahan_data?.fitness_upto} onChange={(val) => updateField('fitness_upto', val)} disabled={!canEdit} />
                      <EditableField label="PUCC Number" value={editData.pucc_number || inspection?.vaahan_data?.pucc_number} onChange={(val) => updateField('pucc_number', val)} disabled={!canEdit} />
                      <EditableField label="PUCC Valid Upto" value={editData.pucc_valid_upto || inspection?.vaahan_data?.pucc_valid_upto} onChange={(val) => updateField('pucc_valid_upto', val)} disabled={!canEdit} />
                    </div>
                  </Section>
                </div>
              </div>

              {/* Challans Section - Full Width */}
              <Section title="Challans & Fines" icon={AlertTriangle} defaultOpen={true}>
                {/* Fetch Button */}
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm text-gray-500">
                    {challanData?.fetched_at ? (
                      <span>Last fetched: {new Date(challanData.fetched_at).toLocaleString()}</span>
                    ) : (
                      <span>Click to fetch real-time challan data from RTO</span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleFetchChallans}
                    disabled={fetchingChallans || !inspection?.car_number}
                    data-testid="fetch-challans-btn"
                    className="gap-2"
                  >
                    {fetchingChallans ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    {fetchingChallans ? 'Fetching...' : challanData ? 'Refresh' : 'Fetch Challans'}
                  </Button>
                </div>
                
                {/* Challan Results */}
                {challanData && challanData.challan_details?.length > 0 ? (
                  <div className="space-y-3">
                    {/* Summary Bar */}
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                        <span className="font-medium text-red-700">
                          {challanData.pending_count > 0 ? 'Pending Challans Found' : 'Challans Found (All Disposed)'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        {challanData.pending_count > 0 && (
                          <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">
                            {challanData.pending_count} Pending
                          </span>
                        )}
                        {challanData.disposed_count > 0 && (
                          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                            {challanData.disposed_count} Disposed
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Challan List */}
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {challanData.challan_details.map((challan, index) => (
                        <div key={challan.challan_number || index} className={`p-3 border rounded-lg ${challan.challan_status === 'Pending' ? 'border-amber-200 bg-amber-50/50' : 'border-gray-200 bg-white'}`}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800 text-sm">{challan.offense_details || 'Traffic Violation'}</p>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                  <Calendar className="h-3 w-3" />
                                  {challan.challan_date || 'N/A'}
                                </span>
                                {challan.challan_place && (
                                  <span className="flex items-center gap-1 text-xs text-gray-500">
                                    <MapPin className="h-3 w-3" />
                                    {challan.challan_place}
                                  </span>
                                )}
                                {challan.state && (
                                  <span className="text-xs text-gray-400">{challan.state}{challan.rto ? ` - ${challan.rto}` : ''}</span>
                                )}
                              </div>
                              {challan.accused_name && (
                                <p className="text-xs text-gray-400 mt-1">Accused: {challan.accused_name}</p>
                              )}
                              {challan.offense_details_list?.length > 1 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {challan.offense_details_list.map((o, i) => (
                                    <span key={i} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                                      {o.offense_name}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {challan.court_challan && challan.court_name && (
                                <p className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                                  <Scale className="h-3 w-3" /> Court: {challan.court_name}
                                </p>
                              )}
                            </div>
                            <div className="text-right ml-3 shrink-0">
                              <p className="font-semibold text-red-600">{activeCurrencySymbol()}{parseFloat(challan.amount || 0).toLocaleString('en-IN')}</p>
                              <span className={`inline-block text-xs px-2 py-0.5 rounded mt-1 ${
                                challan.challan_status === 'Disposed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {challan.challan_status || 'Pending'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Total Summary */}
                    <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Total Challans:</span>
                          <span className="font-semibold text-gray-800">{challanData.total_challans}</span>
                        </div>
                        {challanData.pending_count > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-red-600">Pending Amount:</span>
                            <span className="font-bold text-red-600">{activeCurrencySymbol()}{challanData.total_pending_amount?.toLocaleString('en-IN')}</span>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-gray-400">Total Amount</span>
                        <p className="font-bold text-gray-800 text-lg">{activeCurrencySymbol()}{challanData.total_amount?.toLocaleString('en-IN')}</p>
                      </div>
                    </div>
                  </div>
                ) : challanData && challanData.total_challans === 0 ? (
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-500" />
                    <div>
                      <p className="font-medium text-green-700">No Challans Found</p>
                      <p className="text-sm text-green-600">This vehicle has no outstanding fines or challans</p>
                    </div>
                  </div>
                ) : !challanData ? (
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <AlertTriangle className="h-6 w-6 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-600">Challan data not fetched</p>
                      <p className="text-sm text-gray-500">Click "Fetch Challans" to check for traffic violations</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-500" />
                    <div>
                      <p className="font-medium text-green-700">No Pending Challans</p>
                      <p className="text-sm text-green-600">This vehicle has no outstanding fines or challans</p>
                    </div>
                  </div>
                )}
              </Section>
              
              {/* Dashboard Photo Modal */}
              <Dialog open={showDashboardPhotoModal} onOpenChange={setShowDashboardPhotoModal}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Dashboard Odometer Photo</DialogTitle>
                  </DialogHeader>
                  <div className="flex flex-col items-center gap-4">
                    {loadingDashboardPhoto ? (
                      <div className="text-center p-8 bg-gray-100 rounded-lg w-full">
                        <Loader2 className="h-12 w-12 mx-auto text-blue-500 animate-spin mb-2" />
                        <p className="text-gray-600">Loading photo...</p>
                      </div>
                    ) : dashboardPhotoData ? (
                      <img 
                        src={dashboardPhotoData.startsWith('data:') ? dashboardPhotoData : dashboardPhotoData} 
                        alt="Dashboard Odometer" 
                        className="max-w-full max-h-96 rounded-lg shadow-lg"
                      />
                    ) : (
                      <div className="text-center p-8 bg-gray-100 rounded-lg w-full">
                        <Camera className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-600">Photo not available</p>
                      </div>
                    )}
                    <div className="text-center">
                      <p className="text-lg font-bold text-blue-900">
                        AI Extracted: {(inspection?.ai_extracted_kms || 0).toLocaleString()} km
                      </p>
                      <p className="text-sm text-gray-500">
                        Confidence: {Math.round((inspection?.ai_extracted_kms_confidence || 0) * 100)}%
                      </p>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              </>)}
            </TabsContent>
            
            {/* Q&A Image Viewer Modal */}
            <Dialog open={showQAImageModal} onOpenChange={setShowQAImageModal}>
              <DialogContent className="max-w-3xl max-h-[90vh]">
                <DialogHeader>
                  <DialogTitle className="text-lg">{qaImageTitle}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center gap-4 overflow-auto">
                  {loadingQAImage ? (
                    <div className="text-center p-8 bg-gray-100 rounded-lg w-full">
                      <Loader2 className="h-12 w-12 mx-auto text-blue-500 animate-spin mb-2" />
                      <p className="text-gray-600">Loading image...</p>
                    </div>
                  ) : qaImageData ? (
                    <img 
                      src={qaImageData} 
                      alt={qaImageTitle} 
                      className="max-w-full max-h-[70vh] rounded-lg shadow-lg object-contain"
                    />
                  ) : (
                    <div className="text-center p-8 bg-gray-100 rounded-lg w-full">
                      <Camera className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600">Image not available</p>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            

    </>
  );
};
import { activeCurrencySymbol } from '@/utils/currency';

export default VehicleTab;
